import React, { MouseEvent, useEffect, useState } from 'react';
import { Button, styled, TextField, Typography } from '@mui/material';
import { NumbersOutlined, ShoppingBagOutlined } from '@mui/icons-material';
import Dialog from 'components/dialogs/Dialog';
import ProductsAutoComplete from 'components/autocomplete/ProductsAutoComplete';
import { toast } from 'react-toastify';
import { Product } from 'types/product';
import { ProcedureProduct } from 'types/procedure';

interface ProcedureProductFormProps {
  onExited: () => void;
  handleAddProduct: (productInfo: ProcedureProduct | null) => void;
  productInfo: ProcedureProduct | null;
  // a quantidade é texto digitado, por isso o valor também pode ser string
  handleChangeProductInfo<K extends keyof ProcedureProduct>(index: K, value: ProcedureProduct[K] | string): void;
  type: 'new' | 'edit';
}

const Container = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
});

const Intro = styled('div')({
  border: '1px solid #eadde1',
  borderRadius: 8,
  padding: 12,
  background: 'linear-gradient(135deg, #fff7fa 0%, #fff 100%)',
  display: 'flex',
  gap: 10,
  alignItems: 'center',
});

const IconBox = styled('div')({
  width: 34,
  height: 34,
  borderRadius: 8,
  background: '#fff4f7',
  color: '#7b1235',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: '0 0 auto',
});

const DialogActions = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
  paddingTop: 4,
});

const ProcedureProductForm: React.FC<ProcedureProductFormProps> = ({
  handleAddProduct,
  onExited,
  productInfo,
  handleChangeProductInfo,
  type,
}) => {
  const [product, setProduct] = useState<Product | null>(null);

  useEffect(() => {
    setProduct(productInfo?.product || null);
  }, [productInfo]);

  function handleValidation(e?: MouseEvent<HTMLButtonElement>) {
    e?.preventDefault();

    if (!product || !productInfo?.product_id) {
      toast.error('Selecione um produto');
      return;
    }

    if (!productInfo.quantity) {
      toast.error('Informe a quantidade');
      return;
    }

    handleAddProduct({
      ...productInfo,
      product: product,
      product_id: product.id,
    });
  }

  return (
    <Dialog
      onExited={onExited}
      disableEscapeKeyDown
      disableBackdropClick
      maxWidth="sm"
      title={type === 'edit' ? 'Editar produto' : 'Adicionar produto'}
    >
      <Container>
        <Intro>
          <IconBox>
            <ShoppingBagOutlined fontSize="small" />
          </IconBox>
          <div>
            <Typography variant="body2" fontWeight={800}>
              Produto avulso
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Selecione o produto e informe a quantidade prevista no procedimento.
            </Typography>
          </div>
        </Intro>

        <ProductsAutoComplete
          label="Produto"
          onSelect={product => {
            handleChangeProductInfo('product_id', product?.id || 0);
            handleChangeProductInfo('product', product);
            setProduct(product);
          }}
          placeholder="Selecione um produto"
          selected={product}
        />

        <TextField
          label="Quantidade"
          placeholder="Digite a quantidade"
          value={productInfo?.quantity || ''}
          onChange={e => handleChangeProductInfo('quantity', e.target.value)}
          fullWidth
          type="number"
          slotProps={{
            input: {
              startAdornment: <NumbersOutlined fontSize="small" style={{ marginLeft: 10, marginRight: 8 }} />,
              inputProps: {
                min: 1,
                step: 1,
              },
            },
          }}
        />

        <DialogActions>
          <Button variant="outlined" color="primary" onClick={onExited}>
            Cancelar
          </Button>
          <Button variant="contained" color="primary" onClick={handleValidation}>
            {type === 'new' ? 'Adicionar' : 'Salvar'}
          </Button>
        </DialogActions>
      </Container>
    </Dialog>
  );
};

export default ProcedureProductForm;
